import { useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useLatest } from 'react-use';

import { MapContext } from '../contexts/MapContext';
import { mapModes } from '../constants/map';
import { fillLayerIds, layerIsCustom } from '../utils/layers';
import { captureEvent } from '../utils/analytics';
import {
  handleBackspaceWhileEditing,
  handleEscapeWhileDrawing,
} from '../utils/draw';
import { showError } from '../utils/toast';

const LAYER_EDIT_ACTIONS = {
  EDIT_FEATURE: 'EDIT_FEATURE',
  DESELECT: 'DESELECT',
};

export default (map) => {
  const { farmId } = useParams();
  const {
    layers: { layers, setLayerData, addLayerFeature },
    mapState: {
      mapMode,
      setMapMode,
      activeLayer: stateActiveLayer,
      activeLayerFeatureId,
      activeLayerFeature,
      layerDrawModeConfig,
      setLayerDrawMode: setDrawMode,
    },
  } = useContext(MapContext);

  const drawRef = useRef();

  const layersRef = useLatest(layers);

  const activeLayer = useRef();
  activeLayer.current = stateActiveLayer;

  const mapModeRef = useRef();
  mapModeRef.current = mapMode;

  const activeLayerFeatureIdRef = useRef();
  activeLayerFeatureIdRef.current = activeLayerFeatureId;

  // Update the draw mode when drawMode state changes
  useEffect(() => {
    const [drawMode, options] = layerDrawModeConfig ?? [];
    if (!drawRef.current || !drawMode) return;

    drawRef.current.changeMode(drawMode, options);
  }, [layerDrawModeConfig]);

  const getAllFeatures = () => drawRef.current.getAll();

  // Update local data as user edits features:
  const handleUpdate = () => {
    const { layerId } = activeLayer.current ?? {};
    if (!layerId) return;

    setLayerData({ layerId, farmId, data: getAllFeatures(), upload: false });
  };

  /**
   * NOTE: handleDone is sometimes called after activeLayer has been reset.
   * In this case, it needs the "stale" state of layerId.
   */
  const potentiallyStaleLayerId = activeLayer.current?.layerId;

  // Update remote data when user finishes editing features:
  const handleDone = () => {
    const layerId = potentiallyStaleLayerId;
    if (!layerId) {
      console.error('handleDone called with no active layer');
      return;
    }

    if (!(layerId in layersRef.current)) {
      // We need to check here to make sure the layer hasn't been removed.
      return;
    }

    // Ensure all features have ids
    const { dataWithIds } = fillLayerIds(getAllFeatures(), layerId);
    captureEvent('layer-drawn', { layerId });

    setLayerData({ layerId, farmId, data: dataWithIds, upload: true });
  };

  const handleLayerEditAction = (action) => {
    const { type, payload = {} } = action;
    const { feature } = payload;

    if (type === LAYER_EDIT_ACTIONS.EDIT_FEATURE) {
      if (mapModeRef.current === mapModes.DRAW_LAYER) {
        // Derive the draw mode from the newly created feature's geometry
        const newMode =
          {
            Point: 'draw_point',
            LineString: 'draw_line_string',
          }[feature.geometry.type] || 'draw_polygon';

        /**
         * NOTE: we don't need to call `setDrawMode()`, since the drawMode isn't
         * changing here. But, Mapbox Draw wants to leave draw mode, but we want
         * to stay in draw mode, so we need to call `changeMode()`. For some
         * reason though, when we call this immediately, mapbox draw does not
         * reset the selected feature for some reason. Calling it again on the
         * next tick fixes this.
         */

        const stayInDrawModeButDeselectFeature = () => {
          drawRef.current.changeMode(newMode);
        };

        stayInDrawModeButDeselectFeature();
        setTimeout(() => {
          stayInDrawModeButDeselectFeature();
        }, 0);

        return;
      }

      if (
        mapModeRef.current !== mapModes.EDIT_LAYER_FEATURE ||
        activeLayerFeatureIdRef.current !== feature.id
      ) {
        setMapMode(mapModes.EDIT_LAYER_FEATURE, feature.id);
      }
    } else if (type === LAYER_EDIT_ACTIONS.DESELECT) {
      // NOTE: we don't need to call handleDone here because that's handled by the useEffect cleanup.
      if (mapModeRef.current === mapModes.EDIT_LAYER_FEATURE) {
        setMapMode(mapModes.LAYER_FEATURE_ENGAGED);
      } else {
        setMapMode(mapModes.LAYER_ENGAGED);
      }
    }
  };

  useEffect(() => {
    if (
      ![
        mapModes.DRAW_LAYER,
        mapModes.EDIT_LAYER,
        mapModes.EDIT_LAYER_FEATURE,
      ].includes(mapMode)
    )
      return undefined;

    const { layerId, data, drawTool } = activeLayer.current;
    const isCustom = layerIsCustom(activeLayer.current);

    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        trash: !activeLayerFeatureId && !isCustom, // TODO: do we need the trash icon for custom layers? do we need it at all?
      },
    });

    map.addControl(drawRef.current);

    if (data) {
      try {
        drawRef.current.add({
          id: layerId,
          ...data,
          // NOTE: MapboxDraw requires ids at the feature level
          features: data.features.map((f) => ({ ...f, id: f.properties.id })),
        });
      } catch (e) {
        showError(e);
        setMapMode(
          activeLayerFeatureId
            ? mapModes.LAYER_FEATURE_ENGAGED
            : mapModes.LAYER_ENGAGED,
          activeLayerFeatureId,
        );
        return () => {
          // reset drawMode
          setDrawMode(null);
          if (drawRef.current) {
            map.removeControl(drawRef.current);
            drawRef.current = null;
          }
        };
      }
    }

    const onSelectionChange = ({ features }) => {
      const [feature] = features;

      if (feature) {
        handleLayerEditAction({
          type: LAYER_EDIT_ACTIONS.EDIT_FEATURE,
          payload: { feature },
        });
      } else {
        handleLayerEditAction({ type: LAYER_EDIT_ACTIONS.DESELECT });
      }
    };

    const handleKeyDown = (e) => {
      if (mapMode === mapModes.EDIT_LAYER_FEATURE) {
        handleBackspaceWhileEditing(e, drawRef.current);
      }
      if (
        [
          mapModes.DRAW_LAYER,
          mapModes.EDIT_LAYER,
          mapModes.EDIT_LAYER_FEATURE,
        ].includes(mapMode)
      ) {
        handleEscapeWhileDrawing(
          e,
          setMapMode,
          mapMode === mapModes.EDIT_LAYER_FEATURE
            ? mapModes.LAYER_FEATURE_ENGAGED
            : mapModes.LAYER_ENGAGED,
        );
      }
    };

    // Update local & remote data when user creates a feature
    const handleCreate = ({ features }) => {
      if (features.length > 1)
        throw new Error(
          'draw.create received multiple features. This is unexpected.',
        );

      const [feature] = features;
      addLayerFeature(layerId, farmId, feature);
    };

    map.on('draw.selectionchange', onSelectionChange);
    map.on('draw.create', handleCreate);
    map.on('draw.update', handleUpdate);

    if (mapMode === mapModes.DRAW_LAYER) {
      setDrawMode(`draw_${drawTool}`);
    } else if (mapMode === mapModes.EDIT_LAYER_FEATURE) {
      if (activeLayerFeature?.geometry?.type === 'Point') {
        // NOTE: 'direct_select' cannot be used with points
        setDrawMode('simple_select', {
          featureIds: [activeLayerFeature.id],
        });
      } else {
        setDrawMode('direct_select', {
          featureId: activeLayerFeatureId,
        });
      }
    }

    if (
      [
        mapModes.DRAW_LAYER,
        mapModes.EDIT_LAYER,
        mapModes.EDIT_LAYER_FEATURE,
      ].includes(mapMode)
    ) {
      // Focus the map canvas so that keyboard events register
      map.getCanvas().focus();
    }

    map.getCanvas().addEventListener('keydown', handleKeyDown);

    return () => {
      // reset drawMode
      setDrawMode(null);

      if (drawRef.current) {
        if (!drawRef.current.getMode().startsWith('draw_')) {
          /**
           * Save the layer if we were editing. Don't save on
           * draw_* because there may be invalid partial features.
           *
           * Also: since we're not saving on draw_* modes, we also don't need to
           * worry about potentially creating a LayerFeature here.
           */
          handleDone();
        }

        map.removeControl(drawRef.current);
      }
      map.off('draw.selectionchange', onSelectionChange);
      map.off('draw.create', handleCreate);
      map.off('draw.update', handleUpdate);
      map.getCanvas().removeEventListener('keydown', handleKeyDown);
      drawRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode]);

  // If the active feature's properties are updated in the state, we need to update
  // them in the draw layer so it doesn't overwrite them when it is saved.
  useEffect(() => {
    if (mapMode === mapModes.EDIT_LAYER_FEATURE) {
      const drawFeature = drawRef.current.get(activeLayerFeatureId);
      Object.entries(activeLayerFeature.properties).forEach(([key, value]) => {
        if (drawFeature.properties[key] !== value) {
          drawRef.current.setFeatureProperty(activeLayerFeatureId, key, value);
        }
      });
    }
  }, [activeLayerFeature?.properties]); // eslint-disable-line react-hooks/exhaustive-deps
};
